<script setup lang="ts">
import { computed, ref } from "vue";
import { useThreadStore } from "@/stores/thread.store";
import SingleThread from "@/components/messaging/SingleThread.vue";
import SendMessageForm from "@/components/messaging/SendMessageForm.vue";
import { Button } from "primevue";
import { useRouter } from "vue-router";
import { useChatWindowStore } from "@/stores/chatWindow.store";
import { useMessageHandler } from "@/components/messaging/useMessageHandler";

const props = defineProps<{
  threadId: number;
  minimized: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "minimize"): void;
  (e: "maximize"): void;
}>();

const threadStore = useThreadStore();
const router = useRouter();
const chatWindowStore = useChatWindowStore();

const thread = computed(() => threadStore.getOpenThread(props.threadId));

const lastThreadTitle = ref("Messages");

const threadTitle = computed(() => {
  const ret = thread.value?.guestName || thread.value?.subject || lastThreadTitle.value;
  lastThreadTitle.value = ret;
  return ret;
});

const sendMessageForm = ref<InstanceType<typeof SendMessageForm> | null>(null);

const {handleNewMessage, handleMessageSuccess, handleMessageFailed, handleUpdateMessage, handleRetryMessage} = useMessageHandler(thread, sendMessageForm);

function toggleMinimize() {
  if (props.minimized) {
    emit("maximize");
  } else {
    emit("minimize");
  }
}

function close() {
  emit("close");
}

function openInInbox() {
  router.push(`/inbox/${props.threadId}`);
  chatWindowStore.closeThread(props.threadId);
}
</script>

<template>
  <div
    class="shadow-lg rounded-t-lg overflow-hidden transition-all duration-200 border-x border-t z-[2]"
    :class="{ 'w-80 h-96': !minimized, 'w-40 h-10': minimized }"
  >
    <header class="flex justify-between items-center bg-surface-200 dark:bg-surface-700 border-b">
      <span class="text-gray-900 dark:text-gray-100 font-semibold truncate p-2" :class="{'cursor-pointer': minimized}" @click="if(minimized)toggleMinimize();">{{ threadTitle }}</span>
      <div class="flex gap-1">
        <Button
          v-if="!minimized"
          icon="pi pi-window-maximize"
          class="p-button-text p-button-rounded p-button-sm -scale-x-100"
          v-tooltip.bottom="'Open in Inbox'"
          @click.stop="openInInbox"
        />
        <Button
          icon="pi pi-minus"
          class="p-button-text p-button-rounded p-button-sm"
          @click.stop="toggleMinimize"
        />
        <Button
          icon="pi pi-times"
          class="p-button-text p-button-rounded p-button-sm"
          @click.stop="close"
        />
      </div>
    </header>
    <div v-if="!minimized" class="flex flex-col h-[calc(100%-40px)] overflow-hidden">
      <div class="overflow-auto grow bg-surface-50 dark:bg-surface-950">
        <SingleThread :thread-id="threadId" class="grow" @retry-message="handleRetryMessage"/>
      </div>
      <div class="shrink-0 border-t bg-surface-100 dark:bg-surface-900">
        <SendMessageForm
          ref="sendMessageForm"
          :threadId="threadId"
          @send-message="handleNewMessage"
          @send-message-success="handleMessageSuccess"
          @send-message-failed="handleMessageFailed"
          @update-message="handleUpdateMessage"
        />
      </div>
    </div>
  </div>
</template>